import { useCallback, useEffect, useRef, useState } from "react";
import agent from "../../agent";

const CUSTOM_POLL_INTERVAL_MS = 5000;
const CUSTOM_POLL_MAX_ATTEMPTS = 500;
const CUSTOM_POLLABLE_STATUSES = new Set(["WAIT", "INPROCESS", "PROCESSING", "PENDING"]);
const ADDITIONAL_DEFAULT_PAGE_SIZE = 5;
const EMPTY_ADDITIONAL_PAGINATION = Object.freeze({
  pageNumber: 0,
  pageSize: ADDITIONAL_DEFAULT_PAGE_SIZE,
  totalElements: 0,
  totalPages: 0,
  numberOfElements: 0,
});
const EMPTY_ADDITIONAL_FILTERS = Object.freeze({
  priceTo: null,
  daysCntTo: null,
  carriers: [],
});
const ROUTE_TYPE_TO_MIX_KEY = Object.freeze({
  OPTIMAL: "MIX_OPT",
  FASTEST: "MIX_TRB",
  CHEAPEST: "MIX_CHEAP",
  FTL: "MIX_FTL",
});

const toSafeNumber = (value) => Number(value) || 0;
const normalizeAdditionalFilters = (filters = {}) => ({
  priceTo: filters?.priceTo ?? null,
  daysCntTo: filters?.daysCntTo ?? null,
  carriers: Array.isArray(filters?.carriers) ? filters.carriers : [],
});

const toRouteArray = (value) => (
  Array.isArray(value) ? value.filter((route) => route && typeof route === "object") : []
);

const normalizeCalcStatus = (status) => String(status || "").trim().toUpperCase();

const sortByPriceDays = (a, b) => {
  const priceDiff = toSafeNumber(a?.price) - toSafeNumber(b?.price);
  if (priceDiff !== 0) {
    return priceDiff;
  }
  return toSafeNumber(a?.daysCnt) - toSafeNumber(b?.daysCnt);
};

const sortByDaysThenPriceDesc = (a, b) => {
  const daysDiff = toSafeNumber(a?.daysCnt) - toSafeNumber(b?.daysCnt);
  if (daysDiff !== 0) {
    return daysDiff;
  }
  return toSafeNumber(b?.price) - toSafeNumber(a?.price);
};

const buildPreferredHighlightsFromRoutes = (routes) => {
  if (!routes.length) {
    return null;
  }

  const typedHighlights = routes.reduce((acc, route) => {
    const mixKey = ROUTE_TYPE_TO_MIX_KEY[String(route?.type || "").trim().toUpperCase()];
    return mixKey && !acc[mixKey] ? { ...acc, [mixKey]: route } : acc;
  }, {});

  if (Object.keys(typedHighlights).length > 0) {
    return {
      MIX_OPT: typedHighlights.MIX_OPT || null,
      MIX_TRB: typedHighlights.MIX_TRB || null,
      MIX_CHEAP: typedHighlights.MIX_CHEAP || null,
      MIX_FTL: typedHighlights.MIX_FTL || null,
    };
  }

  const cheapestRoute = [...routes].sort(sortByPriceDays)[0] || null;
  const cheapestRouteKey = getRouteIdentityKey(cheapestRoute);
  const fastestCandidates = [...routes].sort(sortByDaysThenPriceDesc);
  const fastestRoute = fastestCandidates.find((route) => {
    const routeKey = getRouteIdentityKey(route);
    return routeKey && routeKey !== cheapestRouteKey;
  }) || fastestCandidates[0] || null;
  const fastestRouteKey = getRouteIdentityKey(fastestRoute);

  let optimalRoute = null;
  const unassignedRoutes = routes.filter((route) => {
    const routeKey = getRouteIdentityKey(route);
    return routeKey && routeKey !== cheapestRouteKey && routeKey !== fastestRouteKey;
  });

  if (routes.length === 3) {
    optimalRoute = unassignedRoutes[0] || null;
  }

  return {
    MIX_OPT: optimalRoute,
    MIX_CHEAP: cheapestRoute,
    MIX_TRB: fastestRoute,
    MIX_FTL: null,
  };
};

const getRouteIdentityKey = (route) => {
  if (!route || typeof route !== "object") {
    return "";
  }

  const primaryId = route.num ?? route.id ?? route.routeId ?? route.pathId ?? route.hashCode;
  if (primaryId !== null && primaryId !== undefined && primaryId !== "") {
    return `id:${String(primaryId)}`;
  }

  const firstPath = route?.paths?.[0]?.kato || route?.paths?.[0]?.name || "";
  const lastPath = route?.paths?.[route?.paths?.length - 1]?.kato || route?.paths?.[route?.paths?.length - 1]?.name || "";
  return `route:${firstPath}|${lastPath}|${toSafeNumber(route?.price)}|${toSafeNumber(route?.daysCnt)}|${route?.vectors?.length || 0}`;
};

const mergeUniqueRoutes = (...routeLists) => {
  const mergedMap = new Map();

  routeLists.flat().forEach((route) => {
    if (!route || typeof route !== "object") {
      return;
    }

    const key = getRouteIdentityKey(route);
    if (!key) {
      return;
    }

    mergedMap.set(key, route);
  });

  return Array.from(mergedMap.values());
};

export const parseAsyncRouteResponse = (response) => {
  if (Array.isArray(response)) {
    return {
      status: "",
      hashCodeRequest: null,
      buildPathRequestId: null,
      routes: toRouteArray(response),
      preferredHighlights: null,
      selectedPathId: null,
    };
  }

  const responseData = response?.responseData;
  const responseDataRoutes = Array.isArray(responseData)
    ? toRouteArray(responseData)
    : mergeUniqueRoutes(toRouteArray(responseData?.routes), toRouteArray(responseData?.content));
  const responseRoutes = toRouteArray(response?.routes);
  const dataRoutes = toRouteArray(response?.data);
  const routes = responseDataRoutes.length
    ? responseDataRoutes
    : responseRoutes.length
      ? responseRoutes
      : dataRoutes;

  return {
    status: normalizeCalcStatus(response?.status),
    hashCodeRequest: response?.hashCodeRequest || null,
    buildPathRequestId: response?.id ?? response?.buildPathRequestId ?? null,
    routes,
    preferredHighlights: routes.length > 0
      ? buildPreferredHighlightsFromRoutes(routes)
      : null,
    selectedPathId: (
      responseData && !Array.isArray(responseData)
        ? responseData?.selected?.pathId
        : response?.selected?.pathId
    ) || null,
  };
};

const parseAdditionalRouteResponse = (
  response,
  fallbackPageNumber = 0,
  fallbackPageSize = ADDITIONAL_DEFAULT_PAGE_SIZE
) => {
  if (Array.isArray(response)) {
    const routes = toRouteArray(response);
    return {
      status: "OK",
      hashCodeRequest: null,
      routes,
      pagination: {
        pageNumber: fallbackPageNumber,
        pageSize: fallbackPageSize,
        totalElements: routes.length,
        totalPages: routes.length > 0 ? 1 : 0,
        numberOfElements: routes.length,
      },
    };
  }

  const responseData = response?.responseData;
  const contentRoutes = toRouteArray(responseData?.content);
  const responseDataRoutes = Array.isArray(responseData)
    ? toRouteArray(responseData)
    : toRouteArray(responseData?.routes);
  const responseRoutes = toRouteArray(response?.routes);
  const routes = contentRoutes.length
    ? contentRoutes
    : responseDataRoutes.length
      ? responseDataRoutes
      : responseRoutes;

  const rawPageNumber = Number(responseData?.number ?? responseData?.pageNumber);
  const rawPageSize = Number(responseData?.size ?? responseData?.pageSize ?? responseData?.pageable?.pageSize);
  const pageNumber = Number.isFinite(rawPageNumber) ? Math.max(rawPageNumber, 0) : fallbackPageNumber;
  const pageSize = Number.isFinite(rawPageSize) && rawPageSize > 0 ? rawPageSize : fallbackPageSize;

  const rawTotalElements = Number(responseData?.totalElements);
  const totalElements = Number.isFinite(rawTotalElements) && rawTotalElements >= 0
    ? rawTotalElements
    : routes.length;

  const rawTotalPages = Number(responseData?.totalPages);
  const totalPages = Number.isFinite(rawTotalPages) && rawTotalPages >= 0
    ? rawTotalPages
    : totalElements > 0
      ? Math.ceil(totalElements / pageSize)
      : 0;

  return {
    status: normalizeCalcStatus(response?.status),
    hashCodeRequest: response?.hashCodeRequest || null,
    routes,
    pagination: {
      pageNumber,
      pageSize,
      totalElements,
      totalPages,
      numberOfElements: routes.length,
    },
  };
};

const buildCalcError = (error) => {
  const statusCode = Number(error?.status || error?.response?.status) || null;
  const isGatewayReboot = statusCode === 502;
  const isServerError = statusCode >= 500;
  const isClientError = statusCode >= 400 && statusCode < 500;

  return {
    statusCode,
    title: isGatewayReboot
      ? "Сервис расчёта перезапускается"
      : isServerError
        ? "Ошибка 500: сервис расчёта вернул критическую ошибку"
        : "Маршрут временно недоступен",
    text: isGatewayReboot
      ? "Почти готово: сервер сейчас на перезапуске. Подождите 30-60 секунд и нажмите «Попробовать снова»."
      : isServerError
        ? "Сервер не смог обработать запрос. Это на нашей стороне. Подождите 30-60 секунд и повторите расчёт."
        : isClientError
          ? `Ошибка ${statusCode}. Проверьте параметры расчета и попробуйте снова.`
          : "Не удалось получить расчет. Проверьте параметры и попробуйте еще раз.",
    bannerText: isGatewayReboot
      ? "Сервис на перезапуске"
      : isServerError
        ? "Системная ошибка сервера"
        : null,
  };
};

const buildErrorDebugPayload = ({ requestPayload, error }) => {
  const statusCode = Number(error?.status || error?.response?.status) || null;

  const isGatewayReboot = statusCode === 502;
  const isServerError = statusCode >= 500;
  const isClientError = statusCode >= 400 && statusCode < 500;

  return {
    event: isGatewayReboot
      ? "HTTP_502_REBOOT"
      : isServerError
        ? "HTTP_500"
        : isClientError
          ? "HTTP_4XX"
          : "REQUEST_FAILED",
    statusCode,
    timestamp: new Date().toISOString(),
    request: requestPayload,
    error: {
      message: error?.message || null,
      response: error?.response?.body ?? error?.response?.text ?? null,
    },
  };
};

export const useShoulderCalculator = ({
  fromKato,
  toKato,
  weight,
  cargoValue,
  numPack,
  paidWeight,
  productCategoryCode,
  productName,
  parcelPickupCode,
  parcelDeliveryCode,
  extraServices,
  orgId,
  orgApiKey,
  setFormCollapsed,
  onAfterCalculation,
}) => {
  const [result, setResult] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [calculationHashCodeRequest, setCalculationHashCodeRequest] = useState(null);
  const [calculationBuildPathRequestId, setCalculationBuildPathRequestId] = useState(null);
  const [calculationSelectedPathId, setCalculationSelectedPathId] = useState(null);
  const [customFirst, setCustomFirst] = useState(0);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [calcError, setCalcError] = useState(null);
  const [preferredHighlights, setPreferredHighlights] = useState(null);
  const [isCustomPolling, setIsCustomPolling] = useState(false);
  const [isAdditionalPolling, setIsAdditionalPolling] = useState(false);
  const [customPollingError, setCustomPollingError] = useState(null);
  const [additionalRoutes, setAdditionalRoutes] = useState([]);
  const [additionalPagination, setAdditionalPagination] = useState(EMPTY_ADDITIONAL_PAGINATION);
  const [debugPayload, setDebugPayload] = useState(null);
  const [isDebugCopied, setIsDebugCopied] = useState(false);

  const requestSequenceRef = useRef(0);
  const customPollTimeoutRef = useRef(null);
  const additionalPollTimeoutRef = useRef(null);
  const additionalFiltersRef = useRef(EMPTY_ADDITIONAL_FILTERS);

  const clearCustomPollingTimer = useCallback(() => {
    if (customPollTimeoutRef.current) {
      clearInterval(customPollTimeoutRef.current);
      customPollTimeoutRef.current = null;
    }
  }, []);

  const clearAdditionalPollingTimer = useCallback(() => {
    if (additionalPollTimeoutRef.current) {
      clearInterval(additionalPollTimeoutRef.current);
      additionalPollTimeoutRef.current = null;
    }
  }, []);

  const stopCustomPolling = useCallback((errorMessage = null) => {
    clearCustomPollingTimer();
    setIsCustomPolling(false);
    setCustomPollingError(errorMessage);
  }, [clearCustomPollingTimer]);

  const stopAdditionalPolling = useCallback((errorMessage = null) => {
    clearAdditionalPollingTimer();
    setIsAdditionalPolling(false);
    setCustomPollingError(errorMessage);
  }, [clearAdditionalPollingTimer]);

  const resetAdditionalState = useCallback(() => {
    clearAdditionalPollingTimer();
    setIsAdditionalPolling(false);
    setAdditionalRoutes([]);
    setAdditionalPagination(EMPTY_ADDITIONAL_PAGINATION);
  }, [clearAdditionalPollingTimer]);

  const fetchAdditionalRoutesPage = useCallback((hashCodeRequest, requestId, filters = EMPTY_ADDITIONAL_FILTERS, pageNumber = 0, pageSize = ADDITIONAL_DEFAULT_PAGE_SIZE) => {
    const normalizedFilters = normalizeAdditionalFilters(filters);
    return agent.CalculatorShoulders.calculateAdditionalAsync(
      hashCodeRequest,
      normalizedFilters.priceTo,
      normalizedFilters.daysCntTo,
      normalizedFilters.carriers,
      orgApiKey,
      pageNumber,
      pageSize
    ).then((response) => {
      if (requestSequenceRef.current !== requestId) {
        return null;
      }

      const parsedAdditionalResponse = parseAdditionalRouteResponse(response, pageNumber, pageSize);
      if (parsedAdditionalResponse.hashCodeRequest) {
        setCalculationHashCodeRequest(parsedAdditionalResponse.hashCodeRequest);
      }
      setAdditionalRoutes(parsedAdditionalResponse.routes);
      setAdditionalPagination(parsedAdditionalResponse.pagination);

      return parsedAdditionalResponse;
    });
  }, [orgApiKey]);

  const pollAdditionalRoutes = useCallback((
    hashCodeRequest,
    requestId,
    filters = additionalFiltersRef.current,
    pageNumber = 0,
    pageSize = ADDITIONAL_DEFAULT_PAGE_SIZE
  ) => {
    if (!hashCodeRequest || requestSequenceRef.current !== requestId) {
      return;
    }

    clearAdditionalPollingTimer();
    let attempt = 0;
    setIsAdditionalPolling(true);
    setCustomPollingError(null);

    const runPoll = () => {
      if (requestSequenceRef.current !== requestId) {
        clearAdditionalPollingTimer();
        return;
      }

      if (attempt >= CUSTOM_POLL_MAX_ATTEMPTS) {
        stopAdditionalPolling("Индивидуальные маршруты загружаются дольше обычного. Повторите расчёт, чтобы обновить блок.");
        return;
      }

      attempt += 1;
      fetchAdditionalRoutesPage(hashCodeRequest, requestId, filters, pageNumber, pageSize).then((parsedAdditionalResponse) => {
        if (!parsedAdditionalResponse || requestSequenceRef.current !== requestId) {
          return;
        }

        const isAdditionalReady = parsedAdditionalResponse.status === "OK"
          || (!parsedAdditionalResponse.status && parsedAdditionalResponse.routes.length > 0);
        if (isAdditionalReady) {
          stopAdditionalPolling(null);
          return;
        }

        if (CUSTOM_POLLABLE_STATUSES.has(parsedAdditionalResponse.status)) {
          return;
        }

        stopAdditionalPolling(null);
      }).catch(() => {
        if (requestSequenceRef.current !== requestId) {
          return;
        }
        stopAdditionalPolling("Не удалось догрузить индивидуальные маршруты. Попробуйте открыть раздел позже.");
      });
    };

    runPoll();
    additionalPollTimeoutRef.current = setInterval(runPoll, CUSTOM_POLL_INTERVAL_MS);
  }, [clearAdditionalPollingTimer, fetchAdditionalRoutesPage, stopAdditionalPolling]);

  const pollCustomRoutes = useCallback((hashCodeRequest, requestId) => {
    if (requestSequenceRef.current !== requestId) {
      return;
    }

    clearCustomPollingTimer();
    let attempt = 0;

    const runPoll = () => {
      if (requestSequenceRef.current !== requestId) {
        clearCustomPollingTimer();
        return;
      }

      if (attempt >= CUSTOM_POLL_MAX_ATTEMPTS) {
        stopCustomPolling("Индивидуальные маршруты загружаются дольше обычного. Повторите расчёт, чтобы обновить блок.");
        setLoading(false);
        return;
      }

      attempt += 1;
      agent.CalculatorShoulders.calculateAsyncByHash(hashCodeRequest, orgApiKey).then((pollResponse) => {
        if (requestSequenceRef.current !== requestId) {
          return;
        }

        const parsedPollResponse = parseAsyncRouteResponse(pollResponse);
        if (parsedPollResponse.routes.length > 0) {
          setResult((previous) => mergeUniqueRoutes(previous, parsedPollResponse.routes));
        }
        if (parsedPollResponse.hashCodeRequest) {
          setCalculationHashCodeRequest(parsedPollResponse.hashCodeRequest);
        }
        if (parsedPollResponse.buildPathRequestId !== null && parsedPollResponse.buildPathRequestId !== undefined) {
          setCalculationBuildPathRequestId(parsedPollResponse.buildPathRequestId);
        }
        if (parsedPollResponse.selectedPathId) {
          setCalculationSelectedPathId(parsedPollResponse.selectedPathId);
        }

        if (parsedPollResponse.status === "OK" || parsedPollResponse.routes.length > 0) {
          stopCustomPolling(null);
          setLoading(false);
          pollAdditionalRoutes(parsedPollResponse.hashCodeRequest || hashCodeRequest, requestId, additionalFiltersRef.current, 0, ADDITIONAL_DEFAULT_PAGE_SIZE);
          return;
        }

        if (CUSTOM_POLLABLE_STATUSES.has(parsedPollResponse.status)) {
          if (parsedPollResponse.status !== "WAIT") {
            setLoading(false);
          }
          return;
        }

        stopCustomPolling(null);
        setLoading(false);
      }).catch(() => {
        if (requestSequenceRef.current !== requestId) {
          return;
        }

        stopCustomPolling("Не удалось догрузить индивидуальные маршруты. Базовые тарифы уже готовы, можно продолжать работу.");
        setLoading(false);
      });
    };

    runPoll();
    customPollTimeoutRef.current = setInterval(runPoll, CUSTOM_POLL_INTERVAL_MS);
  }, [clearCustomPollingTimer, orgApiKey, pollAdditionalRoutes, stopCustomPolling]);

  const loadAdditionalRoutesPage = useCallback((pageNumber = 0, pageSize = ADDITIONAL_DEFAULT_PAGE_SIZE, filters = additionalFiltersRef.current) => {
    const requestId = requestSequenceRef.current;
    const hashCodeRequest = calculationHashCodeRequest;
    const normalizedFilters = normalizeAdditionalFilters(filters);

    if (!hashCodeRequest || requestId === 0) {
      return;
    }

    additionalFiltersRef.current = normalizedFilters;
    clearAdditionalPollingTimer();
    setIsAdditionalPolling(true);
    setCustomPollingError(null);

    fetchAdditionalRoutesPage(hashCodeRequest, requestId, normalizedFilters, pageNumber, pageSize).then((parsedAdditionalResponse) => {
      if (!parsedAdditionalResponse || requestSequenceRef.current !== requestId) {
        return;
      }

      const isAdditionalReady = parsedAdditionalResponse.status === "OK"
        || (!parsedAdditionalResponse.status && parsedAdditionalResponse.routes.length > 0);
      if (isAdditionalReady) {
        setIsAdditionalPolling(false);
        return;
      }

      if (CUSTOM_POLLABLE_STATUSES.has(parsedAdditionalResponse.status)) {
        pollAdditionalRoutes(hashCodeRequest, requestId, normalizedFilters, pageNumber, pageSize);
        return;
      }

      setIsAdditionalPolling(false);
    }).catch(() => {
      if (requestSequenceRef.current !== requestId) {
        return;
      }

      setIsAdditionalPolling(false);
      setCustomPollingError("Не удалось получить страницу индивидуальных маршрутов.");
    });
  }, [calculationHashCodeRequest, clearAdditionalPollingTimer, fetchAdditionalRoutesPage, pollAdditionalRoutes]);

  const sendCalculatorForm = useCallback((event, requiredFields) => {
    event?.preventDefault?.();

    if (!requiredFields.every((field) => field)) {
      return false;
    }

    const requestPayload = {
      from: fromKato,
      to: toKato,
      weight,
      cargoValue: cargoValue || 0,
      numPack,
      paidWeight,
      productCategoryCode,
      productName,
      deliveryFromAddress: parcelPickupCode === 1,
      deliveryToAddress: parcelDeliveryCode === 1,
      withPrp: Array.isArray(extraServices) && extraServices.includes("prz"),
      withPrd: Array.isArray(extraServices) && extraServices.includes("prd"),
      orgId,
      isAsc: true,
      sortBy: "PRICE",
    };

    setLoading(true);
    setCalcError(null);
    setDebugPayload(null);

    const requestId = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestId;
    stopCustomPolling(null);
    resetAdditionalState();
    additionalFiltersRef.current = EMPTY_ADDITIONAL_FILTERS;

    setPreferredHighlights(null);
    setCalculationHashCodeRequest(null);
    setCalculationBuildPathRequestId(null);
    setCalculationSelectedPathId(null);

    agent.CalculatorShoulders.calculateAsync(requestPayload, orgApiKey).then((response) => {
      if (requestSequenceRef.current !== requestId) {
        return;
      }

      const parsedResponse = parseAsyncRouteResponse(response);
      const routes = mergeUniqueRoutes(parsedResponse.routes);
      const shouldPollCustom = CUSTOM_POLLABLE_STATUSES.has(parsedResponse.status) && Boolean(parsedResponse.hashCodeRequest);
      const shouldKeepLoading = parsedResponse.status === "WAIT";
      const shouldPollAdditional = !shouldPollCustom
        && Boolean(parsedResponse.hashCodeRequest)
        && (parsedResponse.status === "OK" || parsedResponse.routes.length > 0);

      setResult(routes);
      setCalculationHashCodeRequest(parsedResponse.hashCodeRequest);
      setCalculationBuildPathRequestId(parsedResponse.buildPathRequestId);
      setCalculationSelectedPathId(parsedResponse.selectedPathId);
      setSelectedRow(null);
      setSelectedRoute(null);
      setHasCalculated(true);
      setCalcError(null);
      setPreferredHighlights(parsedResponse.preferredHighlights);
      setIsCustomPolling(shouldPollCustom);
      setCustomPollingError(null);
      setDebugPayload(
        routes.length === 0 && !shouldPollCustom
          ? {
            event: Array.isArray(response) ? "EMPTY_ROUTES" : "EMPTY_RESPONSE_DATA",
            statusCode: 200,
            timestamp: new Date().toISOString(),
            request: requestPayload,
            response: response ?? null,
          }
          : null
      );
      setFormCollapsed?.(routes.length > 0);
      setCustomFirst(0);

      if (shouldPollCustom) {
        pollCustomRoutes(parsedResponse.hashCodeRequest, requestId);
        setLoading(shouldKeepLoading);
      } else if (shouldPollAdditional) {
        pollAdditionalRoutes(parsedResponse.hashCodeRequest, requestId, additionalFiltersRef.current, 0, ADDITIONAL_DEFAULT_PAGE_SIZE);
        setLoading(false);
      } else {
        setLoading(false);
      }

      onAfterCalculation?.();
    }).catch((error) => {
      if (requestSequenceRef.current !== requestId) {
        return;
      }

      stopCustomPolling(null);
      resetAdditionalState();
      setLoading(false);
      setCalculationHashCodeRequest(null);
      setCalculationBuildPathRequestId(null);
      setCalculationSelectedPathId(null);
      setResult([]);
      setSelectedRow(null);
      setSelectedRoute(null);
      setHasCalculated(true);
      setFormCollapsed?.(false);
      setCustomFirst(0);
      setPreferredHighlights(null);
      setDebugPayload(buildErrorDebugPayload({ requestPayload, error }));
      setCalcError(buildCalcError(error));
      onAfterCalculation?.();
    });

    return true;
  }, [
    fromKato,
    toKato,
    weight,
    cargoValue,
    numPack,
    paidWeight,
    productCategoryCode,
    productName,
    parcelPickupCode,
    parcelDeliveryCode,
    extraServices,
    orgId,
    orgApiKey,
    pollCustomRoutes,
    pollAdditionalRoutes,
    resetAdditionalState,
    setFormCollapsed,
    stopCustomPolling,
    onAfterCalculation,
  ]);

  const resetCalculationState = useCallback(() => {
    requestSequenceRef.current += 1;
    stopCustomPolling(null);
    resetAdditionalState();
    additionalFiltersRef.current = EMPTY_ADDITIONAL_FILTERS;

    setResult([]);
    setLoading(false);
    setSelectedRow(null);
    setSelectedRoute(null);
    setCalculationHashCodeRequest(null);
    setCalculationBuildPathRequestId(null);
    setCalculationSelectedPathId(null);
    setCustomFirst(0);
    setHasCalculated(false);
    setCalcError(null);
    setPreferredHighlights(null);
    setDebugPayload(null);
    setIsDebugCopied(false);
  }, [resetAdditionalState, stopCustomPolling]);

  const copyDebugJson = useCallback(async (debugJson) => {
    if (!debugJson || typeof window === "undefined") {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(debugJson);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = debugJson;
        textarea.setAttribute("readonly", "true");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setIsDebugCopied(true);
    } catch (_) {
      setIsDebugCopied(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      requestSequenceRef.current += 1;
      clearCustomPollingTimer();
      clearAdditionalPollingTimer();
    };
  }, [clearAdditionalPollingTimer, clearCustomPollingTimer]);

  return {
    result,
    loading,
    selectedRow,
    setSelectedRow,
    selectedRoute,
    setSelectedRoute,
    calculationHashCodeRequest,
    calculationBuildPathRequestId,
    calculationSelectedPathId,
    customFirst,
    setCustomFirst,
    hasCalculated,
    calcError,
    preferredHighlights,
    isCustomPolling,
    isAdditionalPolling,
    customPollingError,
    additionalRoutes,
    additionalPagination,
    debugPayload,
    isDebugCopied,
    setIsDebugCopied,
    loadAdditionalRoutesPage,
    sendCalculatorForm,
    resetCalculationState,
    copyDebugJson,
  };
};
